import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { JourneyProgressWidget } from "@/components/dashboard/JourneyProgressWidget";
import { GovernanceHealthWidget } from "@/components/dashboard/GovernanceHealthWidget";
import { SuccessionPathCard } from "@/components/dashboard/SuccessionPathCard";
import { GatheringCard } from "@/components/dashboard/GatheringCard";
import { GovernanceIntentIntake } from "@/components/governance/GovernanceIntentIntake";

import { WorkflowSuggestions } from "@/components/dashboard/WorkflowSuggestions";
import { getUserTier } from "@/lib/tierUtils";
import { getUserExperienceLevel } from "@/lib/dashboardHelpers";
import { trackContentUsage } from "@/lib/workflowHelpers";
import { useJourneyStatus } from "@/hooks/useJourneyStatus";

import { toast } from "sonner";
import { Save, Loader2, TrendingUp, ArrowRight } from "lucide-react";

import type { UserTier } from "@/lib/tierUtils";

const NewDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { journeyCompleted } = useJourneyStatus(user?.id);

  // UNA creation form state
  const [unaName, setUnaName] = useState("");
  const [constitutionDate, setConstitutionDate] = useState("");
  const [unaStatus, setUnaStatus] = useState("Pre-Setup");
  const [missionStatement, setMissionStatement] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveUna = async () => {
    if (!user?.id || !unaName.trim()) {
      toast.error("Please enter a UNA name");
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('unas')
        .insert({
          name: unaName.trim(),
          treasury_secretary_id: user.id,
          constitution_date: constitutionDate || null,
          status: unaStatus,
          mission_statement: missionStatement || null
        });

      if (error) throw error;

      toast.success("UNA created successfully!");
      queryClient.invalidateQueries({ queryKey: ['una-data', user.id] });
      setUnaName("");
      setConstitutionDate("");
      setUnaStatus("Pre-Setup");
      setMissionStatement("");
    } catch (error: any) {
      console.error('Error creating UNA:', error);
      toast.error(error.message || "Failed to create UNA");
    } finally {
      setIsSaving(false);
    }
  };

  // Fetch user tier
  const { data: userTier, isLoading: tierLoading } = useQuery({
    queryKey: ['user-tier', user?.id],
    queryFn: async () => {
      if (!user?.id) return 'Premium' as UserTier;
      return await getUserTier(user.id);
    },
    enabled: !!user,
  });

  // Fetch experience level
  const { data: experienceLevel, isLoading: experienceLoading } = useQuery({
    queryKey: ['experience-level', user?.id],
    queryFn: async () => {
      if (!user?.id) return 'new' as const;
      return await getUserExperienceLevel(user.id);
    },
    enabled: !!user,
  });

  // Fetch UNA data
  const { data: unaData, isLoading: unaLoading } = useQuery({
    queryKey: ['una-data', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('unas')
        .select('*')
        .eq('treasury_secretary_id', user?.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      return data;
    },
    enabled: !!user,
  });

  // Fetch profile for treasury secretary name
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', user?.id)
        .single();

      return data;
    },
    enabled: !!user,
  });


  // Track dashboard view
  useEffect(() => {
    if (user?.id) {
      trackContentUsage('dashboard', 'main', 'viewed');
    }
  }, [user?.id]);

  const isLoading = tierLoading || experienceLoading || unaLoading;

  if (isLoading) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </MainLayout>
    );
  }

  const tier = userTier || 'Premium';
  const experience = experienceLevel || 'new';
  const treasurySecretaryName = profile?.full_name || user?.email?.split('@')[0] || 'Member';

  return (
    <>
      <MainLayout>
        <div className="container max-w-7xl mx-auto px-4 py-8 space-y-6 pb-20 md:pb-6">
          {/* Mobile Header - Hidden on desktop */}
          <div className="md:hidden mb-4">
            <h1 className="text-2xl font-bold">Dashboard</h1>
            <p className="text-sm text-muted-foreground">
              Welcome back, {profile?.full_name || 'Steward'}
            </p>
          </div>

          {/* Create UNA Form (only shown if no UNA exists) */}
          {!unaData && (
            <Card>
              <CardHeader>
                <CardTitle>Create Your UNA</CardTitle>
                <CardDescription>Set up your unincorporated nonprofit association to get started</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="una-name">UNA Name *</Label>
                  <Input
                    id="una-name"
                    placeholder="e.g., The Smith Family UNA"
                    value={unaName}
                    onChange={(e) => setUnaName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="constitution-date">Constitution Date</Label>
                  <Input
                    id="constitution-date"
                    type="date"
                    value={constitutionDate}
                    onChange={(e) => setConstitutionDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select value={unaStatus} onValueChange={setUnaStatus}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pre-Setup">Pre-Setup</SelectItem>
                      <SelectItem value="Setup in Progress">Setup in Progress</SelectItem>
                      <SelectItem value="Active">Active</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mission">Mission Statement (Optional)</Label>
                  <Textarea
                    id="mission"
                    placeholder="Describe the purpose of your UNA..."
                    value={missionStatement}
                    onChange={(e) => setMissionStatement(e.target.value)}
                    rows={3}
                  />
                </div>
                <Button 
                  onClick={handleSaveUna} 
                  disabled={isSaving || !unaName.trim()}
                  className="w-full"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Create UNA
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Universal front door: ask a question or start a record */}
          {unaData?.id && (
            <GovernanceIntentIntake unaId={unaData.id} tone="calm" />
          )}

          {/* This week's gathering */}
          <GatheringCard />




          {/* Journey Progress Widget — only while journey is in progress */}
          {!journeyCompleted && (
            <div className="w-full">
              <JourneyProgressWidget
                userId={user?.id || ''}
                unaId={unaData?.id || ''}
                experienceLevel={experienceLevel || 'new'}
              />
            </div>
          )}

          {/* Quiet reference to Journey once completed */}
          {journeyCompleted && (
            <Link
              to="/journey"
              className="block w-full rounded-lg border border-border bg-card/60 px-4 py-3 hover:bg-muted/50 transition"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm">
                  <TrendingUp className="w-4 h-4 text-gold" />
                  <span className="font-medium">Review your Journey</span>
                  <span className="text-muted-foreground hidden sm:inline">
                    Revisit the foundations any time.
                  </span>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </Link>
          )}

          {/* Governance Health Widget */}
          <div className="w-full">
            <GovernanceHealthWidget />
          </div>

          {/* Succession Path (Phase One) */}
          <div className="w-full">
            <SuccessionPathCard />
          </div>

          {/* Workflow Suggestions */}
          <div className="w-full">
            <WorkflowSuggestions
              userId={user?.id || ''}
              unaId={unaData?.id || ''}
              tier={userTier || 'Premium'}
            />
          </div>

        </div>
      </MainLayout>
    </>
  );
};

export default NewDashboard;

